import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { PipelineUrlSelection } from '../types';

type PipelineUrlStateOptions<TUrlFilters extends PipelineUrlSelection> = {
  decode: (_params: URLSearchParams) => TUrlFilters;
  encode: (_filters: TUrlFilters) => URLSearchParams;
  sanitize: (_filters: Partial<TUrlFilters>) => TUrlFilters;
  getActiveFilterCount: (_filters: TUrlFilters) => number;
  ownedKeys?: readonly string[];
};

const CARD_KEY = 'card';

export const usePipelineUrlState = <TUrlFilters extends PipelineUrlSelection>({
  decode,
  encode,
  sanitize,
  getActiveFilterCount,
  ownedKeys = [],
}: PipelineUrlStateOptions<TUrlFilters>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const decodedFilters = useMemo(() => decode(searchParams), [decode, searchParams]);
  const filters = useMemo(() => sanitize(decodedFilters), [decodedFilters, sanitize]);
  const activeFilterCount = useMemo(
    () => getActiveFilterCount(filters),
    [filters, getActiveFilterCount],
  );
  const selectedCardId = filters.card ?? null;

  const ownedKeyList = ownedKeys.join(',');
  const ownedKeySet = useMemo(
    () => new Set<string>([CARD_KEY, ...(ownedKeyList ? ownedKeyList.split(',') : [])]),
    [ownedKeyList],
  );

  const mergeOwnedParams = useCallback(
    (owned: URLSearchParams) => {
      const next = new URLSearchParams(searchParams);
      for (const key of ownedKeySet) next.delete(key);
      for (const [key, value] of owned) next.append(key, value);
      return next;
    },
    [ownedKeySet, searchParams],
  );

  useEffect(() => {
    const sanitizedSearchParams = mergeOwnedParams(encode(filters));
    if (sanitizedSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(sanitizedSearchParams, { replace: true });
    }
  }, [encode, filters, mergeOwnedParams, searchParams, setSearchParams]);

  const setPipelineSearchParams = useCallback(
    (owned: URLSearchParams) => {
      setSearchParams(mergeOwnedParams(owned), { replace: true });
    },
    [mergeOwnedParams, setSearchParams],
  );

  const setFilters = useCallback(
    (nextFilters: TUrlFilters) => {
      setPipelineSearchParams(encode({ ...nextFilters, card: undefined }));
    },
    [encode, setPipelineSearchParams],
  );

  const resetFilters = useCallback(() => {
    setPipelineSearchParams(new URLSearchParams());
  }, [setPipelineSearchParams]);

  const selectCard = useCallback(
    (cardId: string) => {
      const next = encode(filters);
      next.set(CARD_KEY, cardId);
      setPipelineSearchParams(next);
    },
    [encode, filters, setPipelineSearchParams],
  );

  const closeCard = useCallback(() => {
    setPipelineSearchParams(encode({ ...filters, card: undefined }));
  }, [encode, filters, setPipelineSearchParams]);

  return {
    filters,
    activeFilterCount,
    selectedCardId,
    setFilters,
    resetFilters,
    selectCard,
    closeCard,
  };
};
